import React, { useState } from 'react';
import { Trans } from '@lingui/macro';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useRootStore } from 'src/store/root';

import { ListWrapper } from '../../../../components/lists/ListWrapper';
import { usePositionsDataQuery } from '../../../../hooks/yldr/usePositionsDataQuery';
import { selectEnrichedUniswapPositions } from '../../../../store/positionSelectors';
import { PositionsList } from '../../../../components/PositionsList/PositionsList';
import { ToggleButton } from '../../../../components/primitives/ToggleButton/ToggleButton';
import { useDisplayedPairsQuery } from '../../../../hooks/yldr/useDisplayedPairsQuery';
import { EControlPositionType, EPoolType } from '../../../../types/uniswapTokens';
import { EMarketName } from '../../../../ui-config/marketsConfig';
import { TextWithTooltip } from '../../../../components/TextWithTooltip';
import { useProtocolDataContext } from '../../../../hooks/useProtocolDataContext';
import { ToggleButtonGroup } from '../../../../ui-kit/ToggleButtonGroup';
import { LeverageActions } from '../../../../components/PositionsList/components/UniswapPositionsActions/LeverageActions';
import { CONTROL_POSITION_TYPE_FILTERS, POOL_MARKET_FILTERS, POOL_TYPE_FILTERS } from './constants';
import { IControlPositionTypeFilter, IPoolMarketFilter, IPoolTypeFilter } from './types';
import { ToLeveragePositionsSkeleton } from './ToLeveragePositionsSkeleton';

const head = [
  {
    title: <Trans>Pool</Trans>,
    sortKey: 'pool',
  },
  {
    title: <Trans key="Type">Type</Trans>,
    sortKey: 'type',
  },
  {
    title: <Trans key="Liquidity">Liquidity</Trans>,
    sortKey: 'liquidity',
  },
  {
    title: (
      <>
        <Trans key="Max Net APR">Max Net APR</Trans>
        <TextWithTooltip wrapperProps={{ ml: 1 }}>
          <>
            <p>The Max Net APR indicates the anticipated annualized rate that a liquidity provider could have earned over the past 24 hours.</p>
            <p>For stable pools, the Max Net APR calculations are based on a position within the range of [-0.5%; 0.5%], whereas for volatile pools, a position within the range of [-10%; 10%] is applied.</p>
            <p>Please note that this is a projected value, and it may deviate from your actual rate, either lower or higher.</p>
          </>
        </TextWithTooltip>
      </>
    ),
    sortKey: 'maxApr',
  },
  {
    title: <Trans key="maxLeverage">Max Leverage</Trans>,
    sortKey: 'maxLeverage',
  },
];

const FilterToggleButton = ({ key, value, label, ...rest }: { key: React.Key, value: string; label: React.ReactNode }) => (
  <ToggleButton
    key={key}
    value={value}
    size="small"
    sx={{
      width: { xs: '33%', md: 'unset' },
      minWidth: 'min-content',
      flex: '1 1 auto',
      textWrap: 'nowrap',
      px: { xs: 2, md: 3 },
    }}
    {...rest}
  >
    {label}
  </ToggleButton>
);

export const ToLeveragePositionsList = () => {
  const [typeFilter, setTypeFilter] = useState<IPoolTypeFilter>(POOL_TYPE_FILTERS[0]);
  const [controlPositionFilter, setControlPositionFilter] = useState<IControlPositionTypeFilter>(CONTROL_POSITION_TYPE_FILTERS[0]);
  const [marketFilter, setMarketFilter] = useState<IPoolMarketFilter>(POOL_MARKET_FILTERS[0]);
  const { currentChainId: chainId } = useProtocolDataContext();
  const { data: uniswapPositionsData, isLoading: isLoadingPositions } = usePositionsDataQuery();
  const { data: displayedPairs, isLoading: isLoadingPools } = useDisplayedPairsQuery();
  const { breakpoints } = useTheme();
  const md = useMediaQuery(breakpoints.up('md'));

  const enrichedPositionsData = useRootStore((state) => selectEnrichedUniswapPositions(state, uniswapPositionsData));

  const filteredEnrichedPositionData = enrichedPositionsData
    .filter((position) => position.marketReferenceCurrencyValue > 0)
    .map((position) => {
      const poolType = displayedPairs.find((pair) => {
        return pair.token0.underlyingAsset === position.token0.underlyingAsset &&
          pair.token1.underlyingAsset === position.token1.underlyingAsset;
      })?.poolType;
      return { ...position, poolType }
    });

  const filteredMarketFilters = POOL_MARKET_FILTERS
    .filter((market) => market.chainIds.includes(chainId));

  const displayPositions = [
    ...filteredEnrichedPositionData,
    ...displayedPairs,
  ];

  const showMarketFilter = filteredMarketFilters.length > 2;

  const onTypeFilterChangeHandler = (_event: unknown, value: EPoolType) => {
    setTypeFilter(POOL_TYPE_FILTERS.find(
      (i) => i.value === value) || POOL_TYPE_FILTERS[0]
    );
  };

  const onControlPositionFilterChangeHandler = (_event: unknown, value: EControlPositionType) => {
    setControlPositionFilter(CONTROL_POSITION_TYPE_FILTERS.find(
      (i) => i.value === value) || CONTROL_POSITION_TYPE_FILTERS[0]
    );
  };

  const onMarketFilterChangeHandler = (_event: unknown, value: EMarketName) => {
    setMarketFilter(POOL_MARKET_FILTERS.find(
      (i) => i.value === value) || POOL_MARKET_FILTERS[0]
    );
  };

  if (isLoadingPositions || isLoadingPools) {
    return (
      <ToLeveragePositionsSkeleton
        title={<Trans>Positions to Leverage</Trans>}
        head={head.map((col) => col.title)}
      />
    )
  }

  return (
    <ListWrapper
      titleComponent={
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            component="div"
            variant="h3"
            sx={{
              mr: 4,
              mb: { xs: 0, md: 2 }
            }}
          >
            <Trans>Positions to Leverage</Trans>
          </Typography>
        </Box>
      }
      filterComponent={(
        <Box sx={{
          display: 'flex',
          gap: { xs: 3, md: 4 },
          justifyContent: 'end',
          flexDirection: { xs: 'column', md: 'row' }
        }}>
          {showMarketFilter && (
            <ToggleButtonGroup
              color="primary"
              exclusive
              value={marketFilter.value}
              onChange={onMarketFilterChangeHandler}
              sx={{
                mt: { xs: 4, md: 'unset' },
              }}
            >
              {filteredMarketFilters.map((filter) => (
                <FilterToggleButton
                  key={filter.value}
                  value={filter.value}
                  label={(
                    <>
                      {filter.icon}
                      {filter.label}
                    </>
                  )}
                />
              ))}
            </ToggleButtonGroup>
          )}
          <ToggleButtonGroup
            color="primary"
            exclusive
            value={controlPositionFilter.value}
            onChange={onControlPositionFilterChangeHandler}
            sx={{
              mt: { xs: showMarketFilter ? 'unset' : 4, md: 'unset' },
              mb: { xs: 1, md: 'unset' },
            }}
          >
            {CONTROL_POSITION_TYPE_FILTERS.map((filter) => (
              <FilterToggleButton
                key={filter.value}
                value={filter.value}
                label={filter.label}
              />
            ))}
          </ToggleButtonGroup>
          <ToggleButtonGroup
            color="primary"
            exclusive
            value={typeFilter.value}
            onChange={onTypeFilterChangeHandler}
            sx={{
              mt: { xs: showMarketFilter ? 'unset' : 4, md: 'unset' },
              mb: { xs: 1, md: 'unset' },
            }}
          >
            {POOL_TYPE_FILTERS.map((filter) => (
              <FilterToggleButton
                key={filter.value}
                value={filter.value}
                label={filter.label}
              />
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
      localStorageName="leveragedPositionsTableCollapse"
      noData={!displayPositions.length}
      hasCollapseButton={!md}
    >
      <PositionsList
        head={head}
        positions={displayPositions}
        actions={(args) => <LeverageActions {...args} />}
        columnsType='leverage'
        typeFilter={typeFilter}
        marketFilter={marketFilter}
        controlPositionFilter={controlPositionFilter}
      />
    </ListWrapper>
  );
};
